import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import type { components } from "@/api/generated/openapi";
import { API_BASE_URL } from "@/config";
import { browseFacetParams, type BrowseFilters, type BrowseSort } from "@/lib/browse/browseFilter";
import {
  fetchCursorPage,
  fetchFirstPage,
  nextPlannedCursor,
  type PlannedCursor,
  type PlannedPage,
  priceIntent,
  priceParams,
  usePriceBoundsKey,
  useServerPriceKnowledge,
  useServerPriceSort,
  walks,
} from "@/lib/browse/serverPriceSort";
import {
  fieldsMatch,
  isRecord,
  isStringOrNull,
  optionalFields,
  validatedJson,
} from "@/lib/ui/validatedJson";

export const BROWSE_PAGE = 40;
/** The page size once a facet or price order walks the rest of the table: the route's 200 cap,
 *  since each page is a sequential round trip from a phone (TRO-1856). */
export const BROWSE_WALK_PAGE = 200;

/**
 * The Pull Sheet public browse endpoint (#339, #340, ADR-0016) uses the same wire shape as the
 * backend's `PublicStockItemOut` and `PublicBrowsePageOut` (see `schemas.py`). These types are
 * hand-written rather than taken from `@/api/generated/openapi`, because that client mirrors the
 * backend's OpenAPI schema and this public, unauthenticated route is `include_in_schema=False`
 * (like the label-QR redirect, `/c/{public_id}`).
 */
/** Which price the customer-facing `price` field represents, so the client can label it (Ask or
 * Market) instead of showing a bare number. Mirrors the backend `PriceVisibility` enum. */
export type PublicPriceVisibility = "ask" | "market" | "none";
export type PublicEventPhase = "upcoming" | "live";

export type PublicBrowseItem = {
  public_id: string;
  product_name: string;
  qty: number;
  condition: string | null;
  is_graded: boolean;
  grading_company: string | null;
  grade: string | null;
  image_url: string | null;
  product_type: string | null;
  // Catalog set and collector number (#530), for telling two same-name copies apart; null for a
  // generic (non-catalog) unit or a catalog miss.
  set_name: string | null;
  number: string | null;
  // Short printing or finish tag (#540), such as "Reverse Holo" or "1st Edition", for two copies
  // that share a set and number and differ only by variant; null for a generic unit or the default
  // printing.
  variant_label: string | null;
  // The card's game ("pokemon", "lorcana", "riftbound") (#630), which drives the per-location game
  // filter; null for a generic unit or a catalog miss.
  game: string | null;
  // The card's upstream catalog rarity string ("Rare Holo", "Double Rare") (#755), mapped to a
  // brand rarity tier by `catalogRarityToBrand` for the storefront tile's accent. Null for a
  // generic unit or a catalog miss, which reads as unknown rarity, never "common".
  rarity: string | null;
  // The name of the StockGroup the unit is filed in (TRO-1095), which this page labels a Location
  // and filters on. Optional for the same deploy-skew reason as the catalog facts above.
  stock_group_name?: string | null;
  price: string | null;
  currency?: string | null;
};

export type PublicBrowsePage = {
  items: PublicBrowseItem[];
  total: number;
  // The same search without the server's facets (TRO-1856), so a narrowed table reads "N of M".
  // Absent on a backend that predates them.
  table_total?: number;
  available_games?: string[];
  // The public identity of the table a customer scanned into (shop and show), to orient them, and
  // the effective visibility mode the item prices were computed under, to label them.
  shop_name: string;
  shop_logo_url: string | null;
  // The vendor's storefront, which hosts this table under a tab (ADR-0138). Null or absent when
  // they have published none, and the page then shows no tab bar: there is no second room.
  shop_slug?: string | null;
  buylist_published?: boolean;
  // Every table this vendor is running, this one included, so the switcher concurrent shows need
  // (ADR-0012) costs no second request. Absent on a backend that predates it, which reads as one
  // table and hides the switcher.
  live_tables?: PublicLiveTable[];
  event_name: string;
  price_visibility: PublicPriceVisibility;
  show_public_id?: string | null;
  participation_id?: string | null;
  event_phase?: PublicEventPhase;
  // Keyset paging (section 12 of the TRO-475 DDL spec). `next_cursor` is the opaque position to
  // send back for the next page; null ends the walk. Optional for the same deploy-skew reason
  // (ADR-0017) as the item-level catalog facts: both are defaulted on `PublicBrowsePageOut`, so a
  // backend that predates them is still contract-legal.
  next_cursor?: string | null;
  has_more?: boolean;
  // How fresh the stored prices are, and whether the server orders and bounds by them (TRO-2345).
  // Absent on an API that predates them.
  priced_as_of?: string | null;
  prices_changed_at?: string | null;
  server_price_sort?: boolean;
  /** The facets the server narrowed in SQL (TRO-2347); absent from an older server. */
  server_facets?: string[];
};

/** One table a vendor is operating, as the switcher and the arrival chooser name it
 * (ADR-0138). The event id is what `/browse/[eventId]` addresses. */
export type PublicLiveTable = { event_id: string; name: string; };

export type PublicBrowseSetFacet = { game: string; name: string; };
export type PublicBrowseGames = {
  games: string[];
  sets?: PublicBrowseSetFacet[];
  stock_groups?: string[];
};

export function isLiveTable(value: unknown): value is PublicLiveTable {
  return isRecord(value) && typeof value.event_id === "string" && typeof value.name === "string";
}

function isSetFacet(value: unknown): value is PublicBrowseSetFacet {
  return isRecord(value) && typeof value.game === "string" && typeof value.name === "string";
}

export function isPublicBrowseGames(value: unknown): value is PublicBrowseGames {
  return isRecord(value) && Array.isArray(value.games)
    && value.games.every((game) => typeof game === "string")
    && (value.sets === undefined || (Array.isArray(value.sets) && value.sets.every(isSetFacet)))
    && (value.stock_groups === undefined || (Array.isArray(value.stock_groups)
      && value.stock_groups.every((name) => typeof name === "string")));
}

/** Loads the complete game, catalog-set and Location facets separately, so they never delay the
 *  first inventory page. `sets` and `stock_groups` are optional on the wire because the browse SPA
 *  and the API deploy separately (ADR-0017): a `{games}`-only payload still yields the game facet
 *  rather than breaking the whole filter control. */
export function usePublicBrowseGames(eventId: string | null, search = "") {
  const q = search.trim();
  const query = useQuery({
    queryKey: ["public-browse-games", eventId, q],
    enabled: !!eventId,
    queryFn: async (): Promise<PublicBrowseGames> => {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      const suffix = q ? `?${qs.toString()}` : "";
      const resp = await fetch(`${API_BASE_URL}/events/${eventId}/browse/games${suffix}`);
      if (!resp.ok) throw new Error("Couldn't load this event's game filters.");
      return await validatedJson(resp, isPublicBrowseGames, "Game filters response was invalid");
    },
  });
  return {
    games: query.data?.games ?? [],
    sets: query.data?.sets ?? [],
    stockGroups: query.data?.stock_groups ?? [],
  };
}

/** One last-sold (a recent completed sale) on the item-detail screen's last-solds list, in the same
 * wire shape as the backend's `CardSaleOut` (see `schemas.py`). */
export type PublicLastSold = {
  price: string;
  sold_at: string;
  marketplace: string;
  grade_label: string | null;
  url: string;
  flag: "signed" | "error" | "perfect" | null;
};

/** What one source says this scope is worth, in the same wire shape as `SourceQuoteOut`
 * (TRO-775). The browse detail page draws the same per-source stack the Dossier does. */
export type PublicSourceQuote = {
  source: string;
  price: string;
  observed_on: string;
  change_pct?: string | null;
};

/** One grade scope's last-solds (such as "Raw" or "PSA 10"), in the same wire shape as
 * `CardScopeOut`. */
export type PublicLastSoldScope = {
  scope: string;
  market_price: string | null;
  sales: PublicLastSold[];
  median: string | null;
  median_count: number;
  liquidity: "liquid" | "moderate" | "slow" | "insufficient_data";
  /** Optional for the same reason `rarity` is: an API that predates TRO-775 omits the key, and
   *  rejecting the payload over it would replace the whole card with "Item response was invalid".
   *  The stack reads `?? []` and renders every row as an em dash. */
  quotes?: PublicSourceQuote[];
  misprint?: components["schemas"]["MisprintObservationOut"] | null;
};

/** The full last-solds payload for one item's catalog identity, in the same wire shape as
 * `CardDetailOut`. The detail page's `last_solds` field is absent (`None`) for a generic,
 * non-catalog unit, which has no last-solds to fetch. */
export type PublicLastSolds = {
  system: string;
  external_id: string;
  name: string;
  set_name: string | null;
  number: string | null;
  image_url: string | null;
  market_price: string | null;
  // The catalog rarity of the card (#760). The detail page's hero reads rarity from the item
  // (`PublicBrowseItem.rarity`, #755), not from here, but the field is on the wire and this type
  // mirrors `CardDetailOut` exactly, so no reader concludes the field does not exist.
  rarity: string | null;
  subject_scope: string;
  scopes: PublicLastSoldScope[];
};

/** One item's detail-view payload (#341), in the same wire shape as the backend's
 * `PublicItemDetailOut`: the item (the same shape as a `PublicBrowseItem`) plus its
 * outside-marketplace last-solds, or `null` last-solds for a generic, non-catalog unit. */
export type PublicItemDetail = {
  item: PublicBrowseItem;
  last_solds: PublicLastSolds | null;
  // The card's TCGplayer product id (TRO-483), so the detail page's always-rendered "TCGplayer
  // market" row links to the exact product instead of a name-and-set search. Absent or null when
  // the catalog cannot map the card or the unit is generic; the page then falls back to the search.
  tcgplayer_product_id?: number | null;
  // The mode the headline `item.price` was computed under, so the detail page labels it "Asking
  // price" or "Market price"; a premium Ask above the "TCGplayer market" comp then reads as
  // deliberate.
  price_visibility: PublicPriceVisibility;
  show_public_id?: string | null;
  participation_id?: string | null;
  event_phase?: PublicEventPhase;
};

export function isPriceVisibility(value: unknown): value is PublicPriceVisibility {
  return value === "ask" || value === "market" || value === "none";
}

function isEventPhase(value: unknown): value is PublicEventPhase {
  return value === "upcoming" || value === "live";
}

function isPublicBrowseItem(value: unknown): value is PublicBrowseItem {
  return isRecord(value) && typeof value.public_id === "string"
    && typeof value.product_name === "string" && typeof value.qty === "number"
    && typeof value.is_graded === "boolean"
    && fieldsMatch(value, ["condition"], isStringOrNull)
    // Absent, not only null: every field below has a backend default (`PublicStockItemOut`), and
    // the browse SPA deploys separately from the API (ADR-0017), so an API that predates a field
    // (most recently `rarity`, #755) omits it. A missing catalog fact already reads as unknown;
    // rejecting the whole page would blank a customer's storefront during an expected deploy skew.
    && optionalFields(
      value,
      [
        "grading_company",
        "grade",
        "image_url",
        "product_type",
        "set_name",
        "number",
        "variant_label",
        "game",
        "rarity",
        "stock_group_name",
        "price",
        "currency",
      ],
      isStringOrNull,
    );
}

function isPublicBrowsePage(value: unknown): value is PublicBrowsePage {
  return isRecord(value) && Array.isArray(value.items) && value.items.every(isPublicBrowseItem)
    && typeof value.total === "number"
    && (value.table_total === undefined || typeof value.table_total === "number")
    && (value.available_games === undefined || (Array.isArray(value.available_games)
      && value.available_games.every((game) => typeof game === "string")))
    && typeof value.shop_name === "string"
    // Defaulted on the backend like the item-level catalog facts above, so absent is legal here
    // too; a shop that has uploaded no logo is not a broken table.
    && optionalFields(value, ["shop_logo_url", "shop_slug"], isStringOrNull)
    && (value.buylist_published === undefined || typeof value.buylist_published === "boolean")
    && optionalFields(value, ["show_public_id", "participation_id"], isStringOrNull)
    && (value.live_tables === undefined
      || (Array.isArray(value.live_tables) && value.live_tables.every(isLiveTable)))
    && (value.event_phase === undefined || isEventPhase(value.event_phase))
    && optionalFields(value, ["next_cursor", "priced_as_of", "prices_changed_at"], isStringOrNull)
    && (value.has_more === undefined || typeof value.has_more === "boolean")
    && (value.server_price_sort === undefined || typeof value.server_price_sort === "boolean")
    && (value.server_facets === undefined
      || (Array.isArray(value.server_facets)
        && value.server_facets.every((facet) => typeof facet === "string")))
    && typeof value.event_name === "string" && isPriceVisibility(value.price_visibility);
}

function isPublicLastSold(value: unknown): value is PublicLastSold {
  return isRecord(value) && fieldsMatch(
    value,
    ["price", "sold_at", "marketplace", "url"],
    (field) => typeof field === "string",
  )
    // Defaulted on `CardSaleOut`, so absent is contract-legal for the same deploy-skew reason
    // (ADR-0017) as the browse item's catalog facts. The row already reads a missing grade label
    // as raw and a missing flag as not flagged.
    && optionalFields(value, ["grade_label"], isStringOrNull)
    && (value.flag === undefined || value.flag === "signed" || value.flag === "error"
      || value.flag === "perfect" || value.flag === null);
}

function isPublicSourceQuote(value: unknown): value is PublicSourceQuote {
  return isRecord(value) && typeof value.source === "string" && typeof value.price === "string"
    && typeof value.observed_on === "string"
    && optionalFields(value, ["change_pct"], isStringOrNull);
}

function isMisprintSale(value: unknown): boolean {
  return isRecord(value)
    && typeof value.sale_id === "string" && /^[1-9]\d*$/.test(value.sale_id)
    && typeof value.price === "string" && Number.isFinite(Number(value.price))
    && Number(value.price) > 0
    && typeof value.sold_at === "string" && Number.isFinite(Date.parse(value.sold_at))
    && typeof value.quantity === "number" && Number.isSafeInteger(value.quantity)
    && value.quantity > 0;
}

function isPublicLastSoldScope(value: unknown): value is PublicLastSoldScope {
  return isRecord(value) && typeof value.scope === "string"
    // `market_price` and `median` default to None on `CardScopeOut`. `median_count` and `liquidity`
    // default to a value (0, "insufficient_data") the screen reads as a number and an enum, so
    // those two stay required rather than letting `undefined` reach code that expects one.
    && optionalFields(value, ["market_price", "median"], isStringOrNull)
    && Array.isArray(value.sales) && value.sales.every(isPublicLastSold)
    && typeof value.median_count === "number"
    && (value.liquidity === "liquid" || value.liquidity === "moderate"
      || value.liquidity === "slow" || value.liquidity === "insufficient_data")
    // Absent is fine (an API that predates TRO-775); present must be well-formed, or the stack
    // would read prices off objects that have none.
    && (value.misprint == null
      || (isRecord(value.misprint) && optionalFields(
        value.misprint,
        ["market_price", "card_url", "reported_at", "source_updated_at", "scope_label"],
        isStringOrNull,
      ) && (value.misprint.recent_sales === undefined
        || (Array.isArray(value.misprint.recent_sales) && value.misprint.recent_sales.length <= 10
          && value.misprint.recent_sales.every(isMisprintSale)))))
    && (value.quotes === undefined || value.quotes === null
      || (Array.isArray(value.quotes) && value.quotes.every(isPublicSourceQuote)));
}

function isPublicLastSolds(value: unknown): value is PublicLastSolds {
  return isRecord(value) && fieldsMatch(
    value,
    ["system", "external_id", "name", "subject_scope"],
    (field) => typeof field === "string",
  )
    // Every field here defaults to `None` on `CardDetailOut`, and an API that predates the newest,
    // `rarity` (#760), omits that key. The detail hero reads rarity from the item (#755), not from
    // here, so a missing one costs nothing; rejecting the payload over it would replace the whole
    // card with "Item response was invalid".
    && optionalFields(
      value,
      ["set_name", "number", "image_url", "market_price", "rarity"],
      isStringOrNull,
    ) && Array.isArray(value.scopes) && value.scopes.every(isPublicLastSoldScope);
}

function isPublicItemDetail(value: unknown): value is PublicItemDetail {
  return isRecord(value) && isPublicBrowseItem(value.item)
    // `last_solds` defaults to `None` on `PublicItemDetailOut`, and the screen's `last_solds ==
    // null` empty state treats absent and null alike, so absent is the generic-unit state.
    && (value.last_solds == null || isPublicLastSolds(value.last_solds))
    // Absent, null or a number. The browse SPA deploys separately from the API (ADR-0017), so an
    // API that predates TRO-483 omits it and the page falls back to the search link.
    && optionalFields(
      value,
      ["tcgplayer_product_id"],
      (field) => field === null || typeof field === "number",
    )
    && isPriceVisibility(value.price_visibility)
    && optionalFields(value, ["show_public_id", "participation_id"], isStringOrNull)
    && (value.event_phase === undefined || isEventPhase(value.event_phase));
}

/**
 * Fetches one operating Event's public browse page: everything a customer sees after scanning a
 * table's QR (ADR-0017's tracer bullet).
 *
 * Does not use the shared `api` client (`@/api/client`), whose `onRequest` middleware attaches an
 * `Authorization: Bearer <token>` header whenever a signed-in session exists (for example, a staff
 * member previewing their own event's QR while signed into the vendor app). This screen must work
 * identically for an anonymous visitor and must never send any auth state, so it uses a plain
 * `fetch()` with no `Authorization` header. That enforces the "reachable with zero authenticated
 * context" invariant ADR-0017 calls out, rather than relying on nobody being signed in during
 * tests.
 *
 * `eventId` is the event's existing `public_id` (no new id scheme, #340), the same id the vendor's
 * authenticated Show Mode uses. A 404 is thrown as an `Error` that react-query's `error` state
 * carries, and the screen renders a plain not-found message.
 *
 * `search` is what the customer typed (#768), sent to the server as `q` rather than filtered here.
 * So are the facets the server can narrow in SQL (`browseFacetParams`, TRO-1856).
 * The backend matches name, product type and printed collector number in SQL, so a card the client
 * has not loaded is still found instead of reading as a false "no matches". It is part of the query
 * key, and `placeholderData: keepPreviousData` keeps the previous results on screen while the next
 * arrive; without it every keystroke would drop the screen to its skeleton and take the search box
 * and the customer's keyboard focus with it.
 *
 * Loads 40 items first, then pages as the list nears its end. Search, name order and the column
 * facets are server-owned. So are price order and the price range wherever the server reports
 * `server_price_sort` (`serverPriceSort`, TRO-2346), and game and set wherever it lists them in
 * `server_facets` (TRO-2347). Against an older server, game and set, and the price where the server
 * does not sort, load the remaining pages, `BROWSE_WALK_PAGE` units at a time, before the client
 * filters or orders them.
 *
 * Returns the loaded pages flattened into the single-page `PublicBrowsePage` the screen consumes
 * (`data.items` is every unit; the header fields come from the first page), plus `isLoadingRest`,
 * which is true while later pages are loading or a newly typed query is in flight, so the screen
 * can hold back a premature "no cards match".
 */
export function usePublicBrowse(
  eventId: string | null,
  search = "",
  sort: BrowseSort = "featured",
  filters: BrowseFilters = {},
) {
  // Trimmed once, here. The server treats a blank `q` as no constraint, and normalizing before the
  // key makes "charizard" and "charizard " one cached result set rather than two fetches.
  const q = search.trim();
  const known = useServerPriceKnowledge();
  // The game and set facets go to the server once one of its pages listed them (TRO-2347); the
  // query key carries what is sent, so learning it mid-session reloads under them.
  const served = known.serverFacets ?? [];
  const facets = browseFacetParams(filters, served);
  const intent = priceIntent(sort, filters);
  const queryKey = ["public-browse", eventId, q, sort, facets, usePriceBoundsKey(known, intent)];
  const query = useInfiniteQuery({
    queryKey,
    enabled: !!eventId,
    initialPageParam: undefined as PlannedCursor | undefined,
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }): Promise<PlannedPage<PublicBrowsePage>> => {
      // No auth header, as above: a plain `fetch()` with no init argument, never the shared `api`
      // client. The paging and search parameters travel entirely in the query string.
      // The first page stays small so the table paints quickly; only the walk behind it is larger.
      const request = (plan: PlannedCursor["plan"], cursor?: string) => {
        const limit = cursor && walks(plan, intent, served) ? BROWSE_WALK_PAGE : BROWSE_PAGE;
        const qs = new URLSearchParams({ limit: String(limit) });
        if (cursor) qs.set("cursor", cursor);
        if (q) qs.set("q", q);
        for (const [key, value] of priceParams(plan, intent)) qs.set(key, value);
        for (const [key, value] of facets) qs.append(key, value);
        return fetch(`${API_BASE_URL}/events/${eventId}/browse?${qs.toString()}`);
      };
      const parse = (resp: Response) =>
        validatedJson(resp, isPublicBrowsePage, "Browse response was invalid");
      const fail = (resp: Response): never => {
        if (resp.status === 404) throw new Error("This event isn't available.");
        throw new Error("Couldn't load this event's inventory.");
      };
      return pageParam
        ? fetchCursorPage(
          known,
          pageParam.plan,
          () => request(pageParam.plan, pageParam.cursor),
          parse,
          fail,
        )
        : fetchFirstPage(known, intent, (plan) => request(plan), parse, fail);
    },
    // Page by the opaque `next_cursor` the server returns, never by `offset`. A busy table sells a
    // unit while a customer is scrolling, and each sale shifts every later offset page by one, so
    // the next scroll would repeat or skip a unit. A null cursor ends the walk. `total` still comes
    // in the envelope for the header count (ADR-0016) but does not steer paging.
    getNextPageParam: nextPlannedCursor,
  });
  const priced = useServerPriceSort(known, queryKey, query, intent, served);
  const { walking } = priced;

  const {
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isPlaceholderData,
    isRefetching,
    fetchNextPage,
  } = query;
  const pages = query.data?.pages;
  const loadedPages = pages?.length ?? 0;
  useEffect(() => {
    // Stop on a page error rather than retry-looping the public endpoint. `loadedPages` re-runs
    // this after each page, even when a fast response left the fetching flag unchanged. A refetch
    // (the "Prices updated" pill) is left to finish: fetching a page would cancel it.
    if (walking && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && !isRefetching) {
      void fetchNextPage();
    }
  }, [
    walking,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isRefetching,
    fetchNextPage,
    loadedPages,
  ]);

  // Flatten the loaded pages into the one-page shape the browse screen consumes, so nothing
  // downstream needs to know about paging. The header fields (shop, event, visibility, total) are
  // identical across pages, so the first page is their source of truth.
  //
  // Spread the first page rather than naming each header field. The enumeration silently dropped
  // `shop_slug` and `live_tables` when TRO-1174 added them — the screen read `undefined` and hid
  // the tab bar, and no test saw it because the unit suites mock this hook and the backend suite
  // stops at the wire. `items` and `total` still come last, because those are the two the
  // flattening genuinely changes.
  const data = useMemo<PublicBrowsePage | undefined>(() => {
    if (!pages || pages.length === 0) return undefined;
    const first = pages[0].page;
    return {
      ...first,
      available_games: first.available_games ?? [],
      shop_logo_url: first.shop_logo_url ?? null,
      event_phase: first.event_phase ?? "live",
      items: pages.flatMap((p) => p.page.items),
      total: first.total,
    };
  }, [pages]);

  return {
    data,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    // True while later pages of a large table are loading, or while `data` is still the previous
    // query's answer as a newly typed one is in flight (`isPlaceholderData`). Either way the screen
    // holds back "no cards match" and shows a loading state instead. A failed page leaves
    // `hasNextPage` true, so this does not read it, and the screen can settle on what it has.
    isLoadingRest: isFetchingNextPage || isPlaceholderData,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
    // True when the server ordered these pages by price, so the client keeps their order.
    serverPriced: priced.plan === "server",
    // True when a price bound was not sent and the screen narrows the loaded cards by it, so it
    // counts them itself (on main's path, and at a table that shows no prices).
    boundsOnClient: priced.plan !== "server",
    // The game and set facets this load sent, which the server narrowed (`server_facets`, TRO-2347).
    serverFacets: served,
    pricedAsOf: priced.pricedAsOf,
    pricesUpdated: priced.pricesUpdated,
    refreshPrices: priced.refreshPrices,
  };
}

/**
 * Fetches one browsable item's detail view (#341), the tap-through from the browse list: the item's
 * stock image, name and tenant price, plus its outside-marketplace last-solds. Uses a plain
 * `fetch()`, never the shared `api` client, for the same reason as `usePublicBrowse`: it is reached
 * from the same unauthenticated screen, so it must never send a bearer token either.
 *
 * `eventId` and `itemId` are the event's and the item's existing `public_id`s (the browse list's
 * `item.public_id`). A 404 (unknown event, unknown item, or an item that left the event's browsable
 * set since the list was fetched) is thrown as an `Error`.
 */
export function usePublicBrowseItemDetail(eventId: string | null, itemId: string | null) {
  return useQuery({
    queryKey: ["public-browse-item-detail", eventId, itemId],
    enabled: !!eventId && !!itemId,
    queryFn: async (): Promise<PublicItemDetail> => {
      const resp = await fetch(`${API_BASE_URL}/events/${eventId}/browse/${itemId}`);
      if (resp.status === 404) throw new Error("This item isn't available anymore.");
      if (!resp.ok) throw new Error("Couldn't load this item.");
      return await validatedJson(resp, isPublicItemDetail, "Item response was invalid");
    },
  });
}

/**
 * Fetches one card on a vendor's shop for its card page (`/shop/<slug>/item/<id>`): the unit, the
 * shop's own price and the same market comps a table's card detail carries, from
 * `GET /shops/{slug}/items/{id}`. A plain `fetch()`, never the shared `api` client, because the
 * page is unauthenticated. A 404 (unknown shop, or a card the shop no longer lists) is thrown as an
 * `Error`.
 */
/** A 404 from the item lookup: the card sold or was unpublished, as opposed to a failed request. */
export class PublicItemNotListedError extends Error {}

export function usePublicShopItem(slug: string | null, itemId: string | null) {
  return useQuery({
    queryKey: ["public-shop-item", slug, itemId],
    enabled: !!slug && !!itemId,
    queryFn: async (): Promise<PublicItemDetail> => {
      const resp = await fetch(
        `${API_BASE_URL}/shops/${encodeURIComponent(slug!)}/items/${encodeURIComponent(itemId!)}`,
      );
      if (resp.status === 404) {
        throw new PublicItemNotListedError("This card isn't listed anymore.");
      }
      if (!resp.ok) throw new Error("Couldn't load this card.");
      return await validatedJson(resp, isPublicItemDetail, "Item response was invalid");
    },
  });
}
